import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './queryClient';
import { useAuthStore, useThemeStore, useProjectStore } from './store';
import Layout from './components/Layout';
import { AssistantContextProvider } from './contexts/AssistantContext';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import Dashboard from './components/Dashboard';
import ProjectsPage from './components/projects/ProjectsPage';
import ProjectDashboard from './components/projects/ProjectDashboard';
import GoDaddySettingsPage from './components/projects/GoDaddySettingsPage';
import ProjectIntegrationsPage from './components/projects/ProjectIntegrationsPage';
import AgentsPage from './components/agents/AgentsPage';
import ModelsPage from './components/models/ModelsPage';
import WorkflowEditor from './components/workflow/WorkflowEditor';
import WorkflowsPage from './components/workflow/WorkflowsPage';

import SettingsPage from './components/settings/SettingsPage';
import ApiKeysSettingsPage from './components/settings/ApiKeysSettingsPage';
import BootcampLanding from './components/bootcamp/BootcampLanding';
import BootcampDocumentation from './components/bootcamp/BootcampDocumentation';
import BootcampTutorials from './components/bootcamp/BootcampTutorials';
import BootcampAssistant from './components/bootcamp/BootcampAssistant';
import KnowledgeBasePage from './components/knowledge/KnowledgeBasePage';
import WhatsNewPage from './components/whats-new/WhatsNewPage';
import RegistriesPage from './components/registries/RegistriesPage';
import QueuesPage from './pages/QueuesPage';
import QueueDetailsPage from './pages/QueueDetailsPage';
import HistoryPage from './pages/HistoryPage';
import ActivityHistoryPage from './pages/ActivityHistoryPage';
import UndoToast from './components/history/UndoToast';
import ExplorePage from './pages/ExplorePage';
import FollowingFeedPage from './pages/FollowingFeedPage';
import UserProfilePage from './pages/UserProfilePage';
import SharedProjectViewPage from './pages/SharedProjectViewPage';
import SharedWorkflowViewPage from './pages/SharedWorkflowViewPage';
import MyTeamsPage from './pages/MyTeamsPage';
import TeamDetailPage from './pages/TeamDetailPage';
import AdminPortalPage from './pages/AdminPortalPage';
import { useNotificationPolling } from './hooks/useNotificationPolling';

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, _hasHydrated } = useAuthStore();
  const location = useLocation();

  // Wait for Zustand persist to hydrate from localStorage before making auth decisions
  // This prevents the race condition where we redirect to /login before auth state is restored
  if (!_hasHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the original URL so we can redirect back after login
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  return <>{children}</>;
};

// Admin Route component - checks is_admin flag, redirects non-admins to dashboard
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthStore();

  if (!user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Public Route component (redirects to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, _hasHydrated } = useAuthStore();

  // Wait for Zustand persist to hydrate from localStorage before making auth decisions
  if (!_hasHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

/**
 * AppRoutes - Contains the route definitions for the application
 * Exported separately to allow testing without the Router wrapper
 */
export const AppRoutes: React.FC = () => {
  const { loadUser, isAuthenticated, _hasHydrated } = useAuthStore();
  const { initSystemListener, applyTheme, resolvedTheme } = useThemeStore();
  const { loadLLMCatalog } = useProjectStore();

  useEffect(() => {
    // Load user on app start
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    // Load LLM catalog once auth has hydrated and user is authenticated.
    // Hoisted to AppRoutes so it fires regardless of which page the user lands on
    // (the previous Dashboard-only call missed users who navigated directly to a
    // project URL, leaving the AgentFormModal model dropdown with only the
    // gpt-4o-mini fallback option).
    if (_hasHydrated && isAuthenticated) {
      loadLLMCatalog();
    }
  }, [_hasHydrated, isAuthenticated, loadLLMCatalog]);

  useEffect(() => {
    // Apply theme on mount and listen for system preference changes
    applyTheme();
    const cleanup = initSystemListener();
    return cleanup;
  }, [initSystemListener, applyTheme]);

  // Start notification polling (auto-fetches on mount, polls every 30s)
  useNotificationPolling();

  return (
    <AssistantContextProvider>
      <div className="App">
        <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="activities" element={<ActivityHistoryPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/:projectId" element={<ProjectDashboard />} />
          <Route path="projects/:projectId/agents" element={<AgentsPage />} />
          <Route path="projects/:projectId/schemas" element={<ModelsPage />} />
          <Route path="projects/:projectId/workflows" element={<WorkflowsPage />} />
          <Route path="projects/:projectId/workflows/new" element={<WorkflowEditor />} />
          <Route path="projects/:projectId/workflows/:workflowId" element={<WorkflowEditor />} />

          <Route path="projects/:projectId/registries" element={<RegistriesPage />} />
          <Route path="projects/:projectId/queues" element={<QueuesPage />} />
          <Route path="projects/:projectId/queues/:queueId" element={<QueueDetailsPage />} />
          <Route path="projects/:projectId/history" element={<HistoryPage />} />
          <Route path="projects/:projectId/integrations" element={<ProjectIntegrationsPage />} />
          <Route
            path="projects/:projectId/godaddy"
            element={
              <ProtectedRoute>
                <GoDaddySettingsPage />
              </ProtectedRoute>
            }
          />
          <Route path="explore" element={<ExplorePage />} />
          <Route path="following" element={<FollowingFeedPage />} />
          <Route path="teams" element={<MyTeamsPage />} />
          <Route path="teams/:teamId" element={<TeamDetailPage />} />
          <Route path="users/:userId" element={<UserProfilePage />} />
          <Route path="shared/projects/:projectId" element={<SharedProjectViewPage />} />
          <Route path="shared/projects/:projectId/workflows/:workflowId" element={<SharedWorkflowViewPage />} />
          <Route path="bootcamp" element={<BootcampLanding />} />
          <Route path="bootcamp/documentation" element={<BootcampDocumentation />} />
          <Route path="bootcamp/tutorials" element={<BootcampTutorials />} />
          <Route path="bootcamp/assistant" element={<BootcampAssistant />} />
          <Route path="knowledge-base" element={<KnowledgeBasePage />} />
          <Route path="whats-new" element={<WhatsNewPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/api-keys" element={<ApiKeysSettingsPage />} />
          <Route path="admin" element={<AdminRoute><AdminPortalPage /></AdminRoute>} />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>

      {/* Global toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: resolvedTheme === 'dark'
            ? { background: '#374151', color: '#f3f4f6', border: '1px solid #4b5563' }
            : { background: '#ffffff', color: '#1f2937', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: resolvedTheme === 'dark' ? '#f3f4f6' : '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: resolvedTheme === 'dark' ? '#f3f4f6' : '#fff',
            },
          },
        }}
      />

      {/* Global UndoToast for version control undo functionality */}
      <UndoToast />
      </div>
    </AssistantContextProvider>
  );
};

/**
 * App - Main application component
 * Wraps the application with the React Query provider (AC-1377 P1.1) and
 * Router for navigation. The QueryClientProvider sits above the router so
 * every route, modal, and descendant hook can call useQueryClient() and read
 * from the shared singleton cache. ReactQueryDevtools mounts only in dev
 * builds (gated on import.meta.env.DEV) so it never ships to production.
 *
 * Note: Tests should use MemoryRouter and render AppRoutes directly.
 */
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppRoutes />
      </Router>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

export default App;